/**
 * HTTP Client Utility with JWT Token Management
 * Handles API requests with automatic token refresh
 */

import { trackAuthRedirectToLogin, trackAuthSessionFailure } from '@/telemetry/authTelemetry'
import { useTelemetry } from '@/telemetry/useTelemetry'

import { buildApiHeaders, isInternalApiUrl, resolveInternalApiUrl } from './apiHeaders'

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const REMEMBER_ME_KEY = 'remember_me'
const INTERNAL_API_PREFIX = 'hltestmanager'

// Determine which storage to use
function getStorage() {
  // Always check localStorage for the remember_me flag
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY)
  // If remember_me is explicitly false, use sessionStorage; otherwise default to localStorage
  return rememberMe === 'false' ? sessionStorage : localStorage
}

// Token refresh interval (check every 5 minutes, refresh 2 minutes before expiry)
let tokenRefreshInterval = null
let isRefreshing = false

/**
 * Get access token from appropriate storage
 */
export function getAccessToken() {
  const storage = getStorage()
  return storage.getItem(ACCESS_TOKEN_KEY)
}

/**
 * Get refresh token from appropriate storage
 */
export function getRefreshToken() {
  const storage = getStorage()
  return storage.getItem(REFRESH_TOKEN_KEY)
}

/**
 * Store tokens in appropriate storage based on rememberMe preference
 */
export function storeTokens(accessToken, refreshToken, rememberMe = null) {
  const resolvedRememberMe =
    rememberMe === null || rememberMe === undefined ? localStorage.getItem(REMEMBER_ME_KEY) !== 'false' : rememberMe

  // Always store the remember_me preference in localStorage
  localStorage.setItem(REMEMBER_ME_KEY, String(resolvedRememberMe))

  // Choose storage based on preference
  const storage = resolvedRememberMe ? localStorage : sessionStorage

  if (accessToken) {
    storage.setItem(ACCESS_TOKEN_KEY, accessToken)
  }
  if (refreshToken) {
    storage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

/**
 * Clear all tokens from both storages
 */
export function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  sessionStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  sessionStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem('user_session')
  sessionStorage.removeItem('user_session')
  // Keep remember_me preference so we know what storage to use on next login
}

/**
 * Decode JWT token to check expiration
 */
function decodeToken(token) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (e) {
    return null
  }
}

/**
 * Check if token is expired or expiring soon (within 2 minutes)
 */
function isTokenExpiringSoon(token) {
  if (!token) return true

  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return true

  // Token is expiring if it expires within 2 minutes
  const expirationTime = decoded.exp * 1000 // Convert to milliseconds
  const currentTime = Date.now()
  const timeUntilExpiry = expirationTime - currentTime

  return timeUntilExpiry < 120000 // 2 minutes
}

/**
 * Refresh access token using refresh token
 */
async function refreshAccessToken() {
  if (isRefreshing) {
    // If already refreshing, wait and return current token
    return getAccessToken()
  }

  const refreshToken = getRefreshToken()

  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  isRefreshing = true

  try {
    const refreshUrl = resolveInternalApiUrl('/auth/refresh')
    const response = await fetch(refreshUrl, {
      method: 'POST',
      headers: buildApiHeaders({
        url: refreshUrl,
        includeAuth: false,
      }),
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) {
      throw new Error('Token refresh failed')
    }

    const data = await response.json()

    // Store new access token
    storeTokens(data.accessToken, null)

    return data.accessToken
  } catch (error) {
    // If refresh fails, clear all tokens and force re-login
    clearTokens()
    throw error
  } finally {
    isRefreshing = false
  }
}

/**
 * Start automatic token refresh
 * Uses a hybrid approach: setTimeout for next refresh + on-demand checking
 */
export function startTokenRefresh() {
  // Clear any existing timeout
  if (tokenRefreshInterval) {
    clearTimeout(tokenRefreshInterval)
    tokenRefreshInterval = null
  }

  scheduleNextRefresh()
}

/**
 * Schedule the next token refresh based on token expiration
 */
function scheduleNextRefresh() {
  const accessToken = getAccessToken()

  if (!accessToken) {
    return
  }

  const decoded = decodeToken(accessToken)
  if (!decoded || !decoded.exp) {
    return
  }

  // Calculate when to refresh (2 minutes before expiry)
  const expirationTime = decoded.exp * 1000
  const refreshTime = expirationTime - 120000 // 2 minutes before expiry
  const currentTime = Date.now()
  const timeUntilRefresh = refreshTime - currentTime

  // If token is already expired or expiring very soon, refresh immediately
  if (timeUntilRefresh <= 0) {
    refreshAccessToken()
      .then(() => {
        // After refresh, schedule the next one
        scheduleNextRefresh()
      })
      .catch(() => {
        // If refresh fails, stop trying
        stopTokenRefresh()
      })
    return
  }

  // Schedule refresh at the calculated time
  tokenRefreshInterval = setTimeout(async () => {
    try {
      await refreshAccessToken()
      // After successful refresh, schedule the next one
      scheduleNextRefresh()
    } catch (error) {
      // If refresh fails, stop trying
      stopTokenRefresh()
    }
  }, timeUntilRefresh)
}

/**
 * Stop automatic token refresh
 */
export function stopTokenRefresh() {
  if (tokenRefreshInterval) {
    clearTimeout(tokenRefreshInterval)
    tokenRefreshInterval = null
  }
}

function appendQueryParams(url: string, params?: Record<string, any>) {
  if (!params || Object.keys(params).length === 0) {
    return url
  }

  const urlObject = new URL(url, window.location.origin)

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return
    }

    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== null && item !== undefined) {
          urlObject.searchParams.append(key, String(item))
        }
      })
      return
    }

    urlObject.searchParams.set(key, String(value))
  })

  return url.startsWith('http') ? urlObject.toString() : `${urlObject.pathname}${urlObject.search}${urlObject.hash}`
}

function getRequestMethod(method: unknown) {
  return typeof method === 'string' && method.trim() ? method.toUpperCase() : 'GET'
}

function sanitizeApiArea(segment: string | undefined) {
  if (!segment) return 'root'

  const normalizedSegment = segment
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()

  if (!normalizedSegment) return 'unknown'
  if (/^\d+$/.test(normalizedSegment)) return 'unknown'
  if (/^[a-f0-9]{16,}$/.test(normalizedSegment)) return 'unknown'

  return normalizedSegment
}

function getApiArea(url: string) {
  try {
    const urlObject = new URL(url, window.location.origin)
    const segments = urlObject.pathname.split('/').filter(Boolean)
    const routeSegments = segments[0] === INTERNAL_API_PREFIX ? segments.slice(1) : segments

    return sanitizeApiArea(routeSegments[0])
  } catch {
    return 'unknown'
  }
}

function trackApiRequestFailure(url: string, method: string, status?: number) {
  const eventParams: Record<string, string | number | boolean> = {
    api_area: getApiArea(url),
    fatal: false,
    method,
  }

  if (typeof status === 'number' && Number.isFinite(status)) {
    eventParams.status = status
  }

  useTelemetry().trackException('api_request_failed', eventParams)
}

function trackApiRequestPerformance(url: string, method: string, status: number, duration: number) {
  useTelemetry().trackPerformance('api_request', Math.round(duration), {
    api_area: getApiArea(url),
    method,
    status,
    success: status >= 200 && status < 400,
  })
}

/**
 * Make an authenticated API request
 * Automatically adds Authorization header and handles token refresh
 */
export async function apiRequest(
  endpoint: string,
  options: { headers?: Record<string, string>; params?: Record<string, any>; [key: string]: any } = {}
) {
  const baseUrl = resolveInternalApiUrl(endpoint)
  const url = appendQueryParams(baseUrl, options.params)
  const requestMethod = getRequestMethod(options.method)
  const isAuthEndpoint = endpoint.includes('/auth/login') || endpoint.includes('/auth/register')
  const shouldHandleAuth = !isAuthEndpoint && isInternalApiUrl(url)
  const shouldTrackApiFailures = !isAuthEndpoint && isInternalApiUrl(url)
  const shouldTrackApiPerformance = !isAuthEndpoint && isInternalApiUrl(url)
  const requestStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()

  const isFormData = options.body instanceof FormData
  let authHeaders: Record<string, string> = {}

  if (shouldHandleAuth) {
    let accessToken = getAccessToken()

    // Proactively refresh if token is expiring soon (< 2 minutes)
    if (accessToken && isTokenExpiringSoon(accessToken) && getRefreshToken()) {
      try {
        accessToken = await refreshAccessToken()
      } catch (error) {
        // If refresh fails, continue with current token and let 401 handler deal with it
        trackAuthSessionFailure('proactive_refresh_failed')
        console.warn('Proactive token refresh failed:', error)
      }
    }

    if (accessToken) {
      authHeaders = { Authorization: `Bearer ${accessToken}` }
    }
  }

  let headers = buildApiHeaders({
    url,
    includeAuth: shouldHandleAuth,
    isFormData,
    extraHeaders: {
      ...authHeaders,
      ...(options.headers || {}),
    },
  })

  // Merge options
  const fetchOptions = {
    ...options,
    params: undefined,
    headers,
  }

  try {
    // Make the request
    let response = await fetch(url, fetchOptions)

    // If 401 and we have a refresh token, try to refresh and retry
    if (response.status === 401 && shouldHandleAuth) {
      if (getRefreshToken()) {
        try {
          // Try to refresh the token
          const newAccessToken = await refreshAccessToken()

          // Retry the original request with new token
          headers = buildApiHeaders({
            url,
            includeAuth: false,
            isFormData,
            extraHeaders: {
              ...(options.headers || {}),
              Authorization: `Bearer ${newAccessToken}`,
            },
          })
          fetchOptions.headers = headers
          response = await fetch(url, fetchOptions)
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          // Store current URL for redirect after login
          trackAuthSessionFailure('refresh_failed')
          trackAuthRedirectToLogin('session_expired')
          const currentPath = window.location.pathname + window.location.search
          if (currentPath && currentPath !== '/login' && currentPath !== '/') {
            localStorage.setItem('redirect_after_login', currentPath)
          }
          clearTokens()
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
          throw new Error('Session expired. Please login again.')
        }
      } else {
        // No refresh token available, store URL and redirect to login
        trackAuthSessionFailure('missing_refresh_token')
        trackAuthRedirectToLogin('session_expired')
        const currentPath = window.location.pathname + window.location.search
        if (currentPath && currentPath !== '/login' && currentPath !== '/') {
          localStorage.setItem('redirect_after_login', currentPath)
        }
        clearTokens()
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        throw new Error('Session expired. Please login again.')
      }
    }

    if (shouldTrackApiPerformance) {
      const requestDuration = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - requestStartedAt
      trackApiRequestPerformance(url, requestMethod, response.status, requestDuration)
    }

    // Parse response
    let data
    try {
      data = await response.json()
    } catch (parseError) {
      // If JSON parsing fails (common for 413 errors), create error with status-specific message
      let errorMessage = `Request failed with status ${response.status}`
      if (response.status === 413) {
        errorMessage = 'Request payload too large. The data exceeds 25MB limit. Please split your import into smaller batches.'
      } else if (response.status === 400) {
        errorMessage = 'Invalid request data. Please check your input and try again.'
      } else if (response.status === 500) {
        errorMessage = 'Server error occurred. Please try again or contact support.'
      }

      if (shouldTrackApiFailures) {
        trackApiRequestFailure(url, requestMethod, response.status)
      }

      const error = new Error(errorMessage) as any
      error.status = response.status
      throw error
    }

    // Handle errors
    if (!response.ok) {
      // Handle specific HTTP status codes with user-friendly messages
      if (response.status === 413) {
        if (shouldTrackApiFailures) {
          trackApiRequestFailure(url, requestMethod, response.status)
        }

        const error = new Error(
          'Request payload too large. The data exceeds 25MB limit. Please split your import into smaller batches.'
        ) as any
        error.status = 413
        error.code = 'PAYLOAD_TOO_LARGE'
        error.details = data
        throw error
      }

      // Handle NestJS error format (error nested in 'error' object)
      const errorData = data.error || data
      const errorMessage = errorData.message || data.message || 'Request failed'

      if (shouldTrackApiFailures) {
        trackApiRequestFailure(url, requestMethod, response.status)
      }

      const error = new Error(errorMessage) as any
      error.code = errorData.code || data.code
      error.status = response.status
      error.details = data
      throw error
    }

    return data
  } catch (error) {
    // Re-throw with additional context
    if (error.message === 'Failed to fetch') {
      if (shouldTrackApiFailures) {
        trackApiRequestFailure(url, requestMethod)
      }
      throw new Error('Cannot connect to server. Please check your connection.')
    }
    throw error
  }
}

/**
 * Convenience methods for different HTTP verbs
 */
export const http = {
  get: (endpoint, options = {}) => apiRequest(endpoint, { ...options, method: 'GET' }),

  post: (endpoint, data, options = {}) => {
    // If data is FormData, pass it directly without stringifying
    const body = data instanceof FormData ? data : JSON.stringify(data)
    return apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body,
    })
  },

  put: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (endpoint, options = {}) => apiRequest(endpoint, { ...options, method: 'DELETE' }),
}

export default http
